import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Link,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import {
  Dialog,
  Tooltip,
  controlGet,
  controlPost,
} from "@lopie/kernel-ui";

import { copiesOf } from "./adopt.jsx";
import { withoutSandboxes } from "./sandbox.js";

/**
 * Installing an app into this org, and taking one down again.
 *
 * Not a screen. The Apps screen is the one place a person looks for an app —
 * what it is allowed to touch, what is deployed, and how to add or remove one
 * — and three screens that all sounded like "my apps" was the thing that made
 * it hard to find anything. This holds the half of that screen which talks to
 * the control plane.
 *
 * ## Why this screen talks to a different API
 *
 * Everything else in this console reads `api.lopie.one`, which answers what
 * an app may touch and what is in the graph. This one reads
 * `control.lopie.one`, which answers what Lopie publishes and what this org
 * has installed — and is the only API in the system that can create
 * infrastructure in another AWS account.
 *
 * Same token, and that is worth saying because it looks wrong. The control
 * plane's authorizer lists exactly one audience and it is this console's
 * client, so a micro-app token gets a 401 there while working fine against
 * the kernel. Intended shape rather than a gap.
 *
 * ## Deploy first, configure second, and the order is forced
 *
 * A data source is recorded against an **instance**, and the instance name
 * does not exist until the subdomain is chosen and the deployment is created.
 * So this screen collects both in one form and sends them in two steps: the
 * deploy, then one `install.configure` per requirement.
 *
 * That ordering has a visible consequence and the screen owns it rather than
 * hiding it. If the deploy succeeds and a source write fails, the app is real
 * and one of its questions is unanswered — which is exactly the state the
 * Setup screen exists to show, so that is where the message points.
 *
 * ## What the questions are, and where they come from
 *
 * From the **kernel**, not from the catalog. A manifest is one decision per
 * app type and exists before any instance does, so `GET /installs` already
 * knows the vault needs people tagged `employee` and that Google is what
 * produces them — with no deployment anywhere. The catalog says what can be
 * installed; the manifest says what it needs; neither holds a copy of the
 * other.
 *
 * ## Status is observed, not assumed
 *
 * Every row here merges a desired-state row this console wrote with a status
 * row the composition wrote. An instance with no status row is `pending`
 * rather than broken — the provisioner has not been reached by the stream
 * yet — and a screen that rendered that as an error would be reporting a
 * queue as a fault.
 */

export const PHASES = {
  pending: {
    label: "Waiting",
    colour: "kernel.attention",
    line: "Queued. Nothing has started building yet — normal for the first minute.",
  },
  provisioning: {
    label: "Building",
    colour: "kernel.attention",
    line: "Being built in your organization's own AWS account. This takes a few minutes.",
  },
  ready: {
    label: "Ready",
    colour: "kernel.permitted",
    line: "Up and running.",
  },
  failed: {
    label: "Failed",
    colour: "kernel.refused",
    line: "Something went wrong while building it. The line below is what it reported.",
  },
  removing: {
    label: "Removing",
    colour: "kernel.attention",
    line: "Being taken down. This finishes on its own; the list updates when it does.",
  },
  removed: {
    label: "Removed",
    colour: "text.secondary",
    line: "Gone. Nothing of it is left, and this name is free to use again.",
  },
  kept: {
    label: "Removed",
    colour: "text.secondary",
    line: "Gone, but its data was kept — so this name stays taken. Remove it again and delete the data to free it.",
  },
};

/** The subdomain a display name will become, computed the way the API does. */
export function slugOf(displayName) {
  return (displayName || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 21);
}

export function Label({ children }) {
  return (
    <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
      {children}
    </Typography>
  );
}

/**
 * The install form.
 *
 * Two questions and they are not the same kind of question. The subdomain is
 * infrastructure — it decides a hostname and cannot be changed afterwards
 * without redeploying. The data sources are configuration and can be changed
 * on the Setup screen any time. The form says which is which rather than
 * presenting them as one undifferentiated list of fields.
 */
export function InstallDialog({ open, entry, requirements, onClose, onInstall }) {
  const [displayName, setDisplayName] = useState("");
  const [version, setVersion] = useState("");
  const [sources, setSources] = useState({});
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);

  const versions = entry?.packageVersions || [];
  const chosenVersion = version || entry?.defaultVersion || versions[0] || "";
  const slug = slugOf(displayName);
  const ready = slug.length >= 3 && chosenVersion && !busy;

  const submit = async () => {
    setBusy(true);
    setFailure(null);
    try {
      await onInstall({
        app: entry.app,
        displayName: displayName.trim(),
        packageVersion: chosenVersion,
        sources,
      });
      setDisplayName("");
      setSources({});
    } catch (e) {
      setFailure(e?.body?.detail || e?.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Install {entry?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            label="Name it"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoFocus
            fullWidth
            helperText={
              slug
                ? `It will live at ${slug}.apps.lopie.one. The address is fixed once you install — changing it later means installing again.`
                : "Three letters or more. This becomes its web address."
            }
          />

          {versions.length > 1 ? (
            <TextField
              select
              label="Version"
              value={chosenVersion}
              onChange={(e) => setVersion(e.target.value)}
              fullWidth
              helperText="Which version to install."
            >
              {versions.map((v) => (
                <MenuItem key={v} value={v}>{v}</MenuItem>
              ))}
            </TextField>
          ) : null}

          {requirements.length ? (
            <Box>
              <Label>Where its data comes from</Label>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                This app asked for the information below before it can be
                useful. You can answer now or later on the Setup screen —
                unlike the name above, these can be changed.
              </Typography>
              <Stack spacing={2}>
                {requirements.map((r) => (
                  <TextField
                    key={r.key}
                    select
                    size="small"
                    label={`${r.type}${r.tags.length ? ` · ${r.tags.join(", ")}` : ""}`}
                    value={sources[r.key] || ""}
                    onChange={(e) =>
                      setSources({ ...sources, [r.key]: e.target.value })
                    }
                    fullWidth
                    helperText={r.purpose}
                  >
                    <MenuItem value="">Decide later</MenuItem>
                    <MenuItem value="blank">Start empty — add them in the app</MenuItem>
                    {/* Derived by the kernel from the concept registry, so a
                        connector added to it appears here with no change to
                        this screen. */}
                    {(r.satisfiable_by || []).map((o) => (
                      <MenuItem key={o.source} value={`connector:${o.source}`}>
                        {o.source}
                      </MenuItem>
                    ))}
                  </TextField>
                ))}
              </Stack>
            </Box>
          ) : null}

          {failure ? (
            <Alert severity="error" variant="outlined">{failure}</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!ready}>
          {busy ? "Installing…" : "Install"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Removing an instance, and the two questions are not the same question.
 *
 * Taking the app away is reversible in the sense that matters — install it
 * again and you get another one. Taking its data is not: the vault's pepper
 * is the only thing that can decrypt its items, and there is no copy of it
 * anywhere.
 *
 * So the data is a second, unticked decision rather than a second button.
 * "Remove this" and "remove this and everything it held" are the same act
 * with a different appetite for risk, and two buttons would let somebody
 * reach the second by aiming badly.
 *
 * The name has to be typed. Not ceremony: this list is the only place in the
 * console where one click destroys infrastructure, the rows look alike, and
 * "I clicked the wrong row" is the failure a confirm dialog exists for.
 */
export function RemoveDialog({ open, instance, busy, onClose, onRemove }) {
  const [typed, setTyped] = useState("");
  const [deleteData, setDeleteData] = useState(false);
  const [failure, setFailure] = useState(null);

  const name = instance?.displayName || instance?.name || "";
  const ready = typed.trim() === name && !busy;

  const submit = async () => {
    setFailure(null);
    try {
      await onRemove(instance.name, deleteData);
      setTyped("");
      setDeleteData(false);
    } catch (e) {
      setFailure(e?.body?.detail || e?.message || String(e));
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Remove {name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Everything this app runs on comes down. Its web address stops
            working and nobody can open it any more. What Kernel recorded
            stays: the records it created, and the history of what it did.
          </Typography>

          <TextField
            label={`Type ${name} to confirm`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoFocus
            fullWidth
            disabled={busy}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={deleteData}
                onChange={(e) => setDeleteData(e.target.checked)}
                disabled={busy}
              />
            }
            label="Also delete its data"
          />

          {deleteData ? (
            <Alert severity="warning" variant="outlined"
                   sx={{ borderColor: "kernel.attention" }}>
              <AlertTitle>This one does not come back</AlertTitle>
              Everything it stored is deleted for good. For a vault that
              includes the key its passwords are locked with — there is no
              copy of that key anywhere, so nobody can read them afterwards,
              us included. Leave this unticked and the data stays where it is
              instead.
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Everything it stored stays in your account, with nothing using
              it. That is the careful choice and you can still delete it
              later — but this name stays taken until you do.
            </Typography>
          )}

          {failure ? (
            <Alert severity="error" variant="outlined">{failure}</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button color="error" variant="contained" onClick={submit} disabled={!ready}>
          {busy ? "Removing…" : "Remove"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * What is already here, and what is being built right now.
 *
 * The second half is the whole reason this is not a number. Pressing Install
 * closes the dialog and writes a row; the instance then appears in a list
 * *below the fold*, and the thing that changes on screen is a note at the top
 * of a page somebody has scrolled past. From where they are standing nothing
 * happened, and the reasonable next move is to press Install again.
 *
 * So the card carries it: a spinner and a sentence, on the thing that was
 * just clicked. The button is deliberately not disabled — two deployments of
 * one app is a real thing to want — but it stops being the only thing on the
 * card with anything to say.
 */
export function CatalogCard({ entry, installed, onInstall }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "baseline", flexWrap: "wrap" }}>
        <Typography variant="h2" sx={{ flexGrow: 1 }}>{entry.name}</Typography>
        <Chip size="small" variant="outlined" label={entry.defaultVersion} />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {entry.description}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: "center", flexWrap: "wrap" }}>
        <Button variant="contained" size="small" onClick={() => onInstall(entry)}>
          Install
        </Button>
        {installed.building ? (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <CircularProgress size={16} thickness={5} />
            <Typography variant="body2" sx={{ color: "kernel.attention" }}>
              {installed.building === 1
                ? "One is being set up now."
                : `${installed.building} are being set up now.`}{" "}
              It takes a few minutes; the list below updates on its own.
            </Typography>
          </Stack>
        ) : null}
        {installed.ready ? (
          <Typography variant="body2" color="text.secondary">
            {installed.ready} already installed. Installing again sets up a
            second one at its own address.
          </Typography>
        ) : null}
      </Stack>
    </Paper>
  );
}

/** Removals have three states, and flattening them is what made the row lie.
 *
 * The composition keeps reporting `ready` for a minute or so after the XR is
 * deleted, because nothing has told it otherwise yet — so `desiredState` and
 * not `status` is what a removal is read from. But `absent` alone was too
 * coarse in the other direction: it said "on the way out" about two instances
 * that were already gone, and went on saying it for good, because nothing
 * ever wrote a terminal state.
 *
 * `teardownCompletedAt` is that terminal state, written when the composite
 * actually leaves the cluster. And `nameReusable` splits the end state in two,
 * because "removed" and "removed, and its tables are still sitting there" are
 * different things to have to know before typing the name again.
 */
function removalPhase(instance) {
  if (!instance.teardownCompletedAt) return PHASES.removing;
  return instance.nameReusable ? PHASES.removed : PHASES.kept;
}

/** Minutes an address is new enough that not resolving yet is normal. */
const NEW_ADDRESS_MINUTES = 15;

/** An app is "ready" before its address works, and only saying so is wrong.
 *
 * Ready here means the pieces exist and answer. A brand-new hostname takes a
 * few more minutes to spread, so for that window the link on the row can fail
 * to open while the row says Ready — which looks like a broken product rather
 * than a clock.
 *
 * Saying so on every row would be worse: an install from last week is not
 * waiting on anything, and a caveat that never goes away is one people learn
 * to skip, including on the day it matters. So it is said while it is true.
 */
function readyLine(instance) {
  const made = Date.parse(instance.createdAt || "");
  if (!made || Date.now() - made > NEW_ADDRESS_MINUTES * 60000) {
    return PHASES.ready.line;
  }
  return `${PHASES.ready.line} If the link does not open yet, a brand-new web address takes a few minutes to start working everywhere.`;
}

export function InstanceRow({ instance, appName, first, onRemove, latest,
                              onUpdate, onAutoUpdate, copies, onReplace, onRevert }) {
  const phase = instance.desiredState === "absent"
    ? removalPhase(instance)
    : PHASES[instance.status] || PHASES.pending;
  // What it runs, which is not what it is once a copy has adopted it. The
  // releases it can move to are the copy's, and offering the original's would
  // be offering code it does not run.
  const newest = instance.definition
    ? copies?.find((c) => c.app === instance.definition)?.defaultVersion
    : latest;
  return (
    <Box sx={{ pt: first ? 0 : 2 }}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "baseline", flexWrap: "wrap" }}>
        <Typography sx={{ flexGrow: 1 }}>
          {instance.displayName || instance.name}
        </Typography>
        {(instance.desiredState === "absent"
          ? !instance.teardownCompletedAt
          : instance.status !== "ready" && instance.status !== "failed") ? (
          <CircularProgress size={14} thickness={5} sx={{ color: phase.colour }} />
        ) : null}
        <Chip
          size="small"
          variant="outlined"
          label={phase.label}
          sx={{ color: phase.colour, borderColor: phase.colour }}
        />
        {/* Offered only when there is somewhere to go and the instance is
            settled. An Update button on a row that is still building would
            queue a second version change behind a reconcile that has not
            finished, and the row would then be moving toward two things. */}
        {onUpdate
          && newest
          && instance.desiredState !== "absent"
          && instance.status === "ready"
          && newest !== instance.packageVersion ? (
          <Button size="small" onClick={() => onUpdate(instance, newest)}>
            Update to {newest}
          </Button>
        ) : null}
        {/* Offered when there is a copy to run and the instance is settled and
            still running its own code. Changing what an instance runs while it
            is converging puts two changes in flight at once. */}
        {onReplace
          && copies?.length
          && !instance.definition
          && instance.desiredState !== "absent"
          && instance.status === "ready" ? (
          <Button size="small" onClick={() => onReplace(instance)}>
            Replace with a copy
          </Button>
        ) : null}
        {onRevert && instance.definition && instance.desiredState !== "absent" ? (
          <Button size="small" onClick={() => onRevert(instance)}>
            Put {instance.app} back
          </Button>
        ) : null}
        {onRemove && instance.desiredState !== "absent" ? (
          <Button size="small" color="error" onClick={() => onRemove(instance)}>
            Remove
          </Button>
        ) : null}
      </Stack>
      {/* Offered on a live instance only. A switch on a row being torn down
          would be asking about the future of something that has none, and one
          on an app with no catalog row has nothing to follow. */}
      {onAutoUpdate && instance.desiredState !== "absent" && newest ? (
        <FormControlLabel
          sx={{ ml: 0 }}
          control={
            <Switch
              size="small"
              checked={Boolean(instance.autoUpdate)}
              onChange={(e) => onAutoUpdate(instance, e.target.checked)}
              // The switch's own name, and it has to say what turning it ON
              // does. The first version read "Keep ... on the current
              // version", which is what leaving it OFF does — a screen reader
              // user would have been told the opposite of the control.
              slotProps={{ input: { "aria-label": `Update ${instance.displayName || instance.name} automatically when a new version is published` } }}
            />
          }
          label={
            <Typography variant="caption" color="text.secondary">
              {instance.autoUpdate
                ? "Updates itself when a new version is published"
                : "Update by hand"}
            </Typography>
          }
        />
      ) : null}

      <Typography variant="body2" color="text.secondary">
        {instance.definition
          ? `${instance.definition} · version ${instance.packageVersion}`
          : `${appName || instance.app} · version ${instance.packageVersion}`}
        {instance.observedPackageVersion &&
         instance.observedPackageVersion !== instance.packageVersion ? (
          <Tooltip title="It is still moving to the version you asked for. If this does not clear in a few minutes, the upgrade is stuck.">
            <span> · still running {instance.observedPackageVersion}</span>
          </Tooltip>
        ) : null}
      </Typography>
      {instance.definition ? (
        // Said on the row it is true of, for as long as it is true: this is
        // still the same app by name, address and data, running another app's
        // code, and anybody reading the list should not have to infer it.
        <Typography variant="body2" color="text.secondary">
          Running a copy of {appName || instance.app}. It keeps its name, address and data.
          {instance.adoption?.from
            ? ` Replaced ${instance.adoption.from.app} ${instance.adoption.from.version}`
              + `${instance.adoption.at ? ` on ${instance.adoption.at.slice(0, 10)}` : ""}`
              + `${instance.adoption.by ? ` (${String(instance.adoption.by).replace(/^user:/, "")})` : ""}.`
            : ""}
          {/* The original has moved on since the copy was made, and the copy
              does not have what changed. Said while it is true, because an
              adopted instance does not follow its original's releases. */}
          {instance.adoption?.from?.version && latest && latest !== instance.adoption.from.version
            ? ` ${instance.app} is now at ${latest}; this copy was made from ${instance.adoption.from.version} and does not have what changed since.`
            : ""}
        </Typography>
      ) : null}
      <Typography variant="body2" color="text.secondary">
        {/* Two reasons not to show the stored message.

            On a removal it is stale — "submitted to control plane", written
            when it was being built, and nothing overwrites it because the
            thing that would have is gone.

            On a finished install it is a count of parts. While it builds,
            that count is the only progress there is and is worth having; once
            the chip says Ready it is noise the reader has to decode. */}
        {instance.desiredState === "absent"
          ? phase.line
          : instance.status === "ready"
            ? readyLine(instance)
            : instance.message || phase.line}
      </Typography>
      {/* Not on a removal. The URL is the composition's last word and nothing
          overwrites it, so a removed instance keeps offering a link to a host
          that no longer resolves — which reads as "still up" to anyone who
          does not click it, and as a broken product to anyone who does. */}
      {instance.appUrl && instance.desiredState !== "absent" ? (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          <Link href={instance.appUrl} target="_blank" rel="noreferrer">
            {instance.appUrl}
          </Link>
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * Everything the Apps screen needs to install and remove.
 *
 * A hook rather than a component because the catalog, the deployments and the
 * ceilings have to be laid out together — one card per app, carrying all
 * three — and a component that owned the data would have to render it too.
 */
export function useDeployments({ session, get, post, onEffective }) {
  const [catalog, setCatalog] = useState(null);
  const [instances, setInstances] = useState([]);
  const [installs, setInstalls] = useState([]);
  const [failure, setFailure] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chosen, setChosen] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [note, setNote] = useState(null);
  // Which instance the adopt and revert dialogs are about.
  const [adopting, setAdopting] = useState(null);
  const [reverting, setReverting] = useState(null);

  const token = session?.token;
  const control = useMemo(() => (token ? controlGet(token) : null), [token]);
  const controlWrite = useMemo(() => (token ? controlPost(token) : null), [token]);

  const load = useCallback(async () => {
    if (!control) return;
    setLoading(true);
    try {
      const [cat, inst] = await Promise.all([
        control("/catalog"),
        control("/instances"),
      ]);
      setCatalog(cat?.catalog || []);
      // A sandbox is not an install (LO-190). The control plane leaves its
      // own out, and this is the second line: one that got through would be
      // listed as an app, counted as running, and keep this screen polling for
      // a status that is not an install's.
      setInstances(withoutSandboxes(inst?.instances));
      setFailure(null);
    } catch (e) {
      setFailure(e);
    } finally {
      setLoading(false);
    }
  }, [control]);

  useEffect(() => {
    load();
    onEffective(null);
  }, [load, onEffective]);

  // Re-read while anything is moving, and stop when nothing is.
  //
  // Provisioning takes minutes and the composition reports as it goes, so a
  // screen that only read once would show `Waiting` until somebody thought to
  // refresh — which looks exactly like a deploy that never started. The
  // interval exists to make the difference visible.
  //
  // It runs only while something is in flight. A console left open on a quiet
  // org should not poll a control plane forever, and the condition that starts
  // it is the same one that draws the spinner: nothing moving, nothing to
  // watch.
  const moving = instances.some((i) =>
    i.desiredState === "absent"
      // A teardown is in flight until the provisioner says it finished. This
      // is the half that was missing: a removal ran to completion with nobody
      // watching, so the row kept saying `Removing` until the next reload.
      ? !i.teardownCompletedAt
      : i.status !== "ready" && i.status !== "failed",
  );

  useEffect(() => {
    if (!moving) return undefined;
    // Ten seconds. A reconcile writes a status every thirty or so, and polling
    // faster buys a fresher answer to a question that changes slower than
    // that.
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [moving, load]);

  // The questions come from the kernel, not from the catalog — a manifest is
  // one decision per app type and exists before any instance of it does, so
  // this knows what the vault needs with nothing deployed anywhere.
  //
  // Failing is not fatal and is not surfaced. Without it the form asks no
  // data-source questions, which the Setup screen recovers; blocking the
  // whole library on it would not be recoverable by anybody.
  useEffect(() => {
    let live = true;
    get("/installs")
      .then((d) => { if (live) setInstalls(d?.installs || []); })
      .catch(() => {});
    return () => { live = false; };
  }, [get]);

  const requirementsFor = useCallback(
    (app) => (installs.find((i) => i.app_type === app)?.requires || []),
    [installs],
  );

  const install = async ({ app, displayName, packageVersion, sources }) => {
    const created = await controlWrite("/instances", {
      app,
      displayName,
      packageVersion,
    });
    const name = created?.instance?.name || created?.name || slugOf(displayName);

    // Then the sources, one call each, against the instance that now exists.
    // Failures here are reported and not rethrown: the app is deployed, and
    // telling somebody their deploy failed because a follow-up write did
    // would be false.
    const unanswered = [];
    for (const [requirement, source] of Object.entries(sources)) {
      if (!source) continue;
      try {
        await post(`/installs/${app}/${requirement}`, { source, instance: name });
      } catch {
        unanswered.push(requirement);
      }
    }

    setChosen(null);
    setNote(
      unanswered.length
        ? `${displayName} is being installed. ${unanswered.length} answer${unanswered.length === 1 ? "" : "s"} did not save — you can give ${unanswered.length === 1 ? "it" : "them"} again on the Setup screen.`
        : `${displayName} is being installed. Watch the status below; it takes a few minutes.`,
    );
    await load();
  };

  /**
   * Move an installed instance onto a published version.
   *
   * This screen has shown the gap for as long as it has had a version line —
   * `still running 0.1.3` under `version 0.1.4` — and offered nothing to close
   * it, because there was no route. Closing it meant an `update-item` against
   * the control-plane table from a shell, which is not a thing a console
   * should require and not a thing most people holding this screen can do.
   *
   * Best effort on the note, like `install`: the write either happened or the
   * control plane said why, and either way the rows are re-read rather than
   * patched, because what the control plane now holds is the answer.
   */
  const update = async (name, packageVersion) => {
    setRemoveBusy(true);
    try {
      await controlWrite(`/instances/${name}/version`, { packageVersion });
      setNote(
        `${name} is moving to ${packageVersion}. It keeps serving the version `
        + "it has until the new one is deployed, so nothing goes down.",
      );
      await load();
    } finally {
      setRemoveBusy(false);
    }
  };

  /**
   * Follow the catalog's default version, or stop following it.
   *
   * Writes a field and nothing else — the provisioner honours it when a
   * catalog row moves. So the note says what changed and, deliberately, what
   * did not: an admin who flips this and sees the version line unchanged has
   * not hit a bug.
   */
  const setAutoUpdate = async (name, enabled) => {
    setRemoveBusy(true);
    try {
      await controlWrite(`/instances/${name}/auto-update`, { enabled });
      setNote(
        enabled
          ? `${name} will move to each new default version on its own. It stays `
            + "on the version it has until the next one is published."
          : `${name} stays on the version it has. You will be offered updates `
            + "here as they are published.",
      );
      await load();
    } finally {
      setRemoveBusy(false);
    }
  };

  /**
   * What replacing an instance's code with a copy's would do. Read only, and
   * made again when it is confirmed: the kernel does not trust a plan sent back.
   */
  const planAdoption = (name, app, version) =>
    get(`/adoptions/${name}/plan?app=${encodeURIComponent(app)}&version=${encodeURIComponent(version)}`);

  const adopt = async (name, app, version) => {
    setRemoveBusy(true);
    try {
      const done = await post(`/adoptions/${name}`, {
        app, version, acknowledge_restore_point: true,
      });
      setAdopting(null);
      setNote(
        `${name} is being rebuilt to run ${app} ${version}. It keeps its name, `
        + "address and data, and serves what it has until the new code is up. "
        + `A restore point was recorded at ${done?.restore_point || "this moment"}.`
        // Said when it is true and not before: the change stands either way, and
        // a governed change with no audit row is something its owner must hear.
        + (done?.audit_recorded === false
          ? " It could not be written to the audit log, so there is no record of who did this; tell whoever runs this kernel."
          : ""),
      );
      await load();
    } finally {
      setRemoveBusy(false);
    }
  };

  const revert = async (name) => {
    setRemoveBusy(true);
    try {
      const done = await post(`/adoptions/${name}/revert`, { confirm: true });
      setReverting(null);
      setNote(
        `${name} is being put back on its own code. Records the copy wrote while it `
        + "ran stay as they are."
        + (done?.audit_recorded === false
          ? " It could not be written to the audit log, so there is no record of who did this; tell whoever runs this kernel."
          : ""),
      );
      await load();
    } finally {
      setRemoveBusy(false);
    }
  };

  const remove = async (name, deleteData) => {
    setRemoveBusy(true);
    try {
      await controlWrite(`/instances/${name}/deprovision`, { deleteData });
      setRemoving(null);
      setNote(
        deleteData
          ? `${name} is being removed, and everything it stored with it.`
          : `${name} is being removed. What it stored stays in your account.`,
      );
      // Re-read rather than patching locally: the row the kernel wrote is
      // what the screen should show, and this is the one action here that
      // cannot be undone by clicking again.
      await load();
    } finally {
      setRemoveBusy(false);
    }
  };

  // The catalog holds the name a person would recognise; a deployment row
  // carries only the app slug, because that is what identifies it to
  // everything underneath. So the lookup lives here rather than the name
  // being copied onto every row.
  const nameOf = (app) => (catalog || []).find((e) => e.app === app)?.name;

  // What exists, which is not what the table holds. A row survives its
  // teardown deliberately — it is the record of what this org asked for — so
  // counting rows once said "2 already here" about two vaults that were both
  // gone. That was history presented as inventory.
  const countFor = (app) => {
    const live = instances.filter(
      (i) => i.app === app && i.desiredState !== "absent");
    return {
      ready: live.filter((i) => i.status === "ready").length,
      // Anything that is neither up nor given up on. `failed` is deliberately
      // not counted as building — a failed install is not in progress, and
      // saying it is would be the screen waiting for something that is not
      // coming.
      building: live.filter(
        (i) => i.status !== "ready" && i.status !== "failed").length,
    };
  };

  // Deployments of one app, newest-looking first: anything live before
  // anything removed, so a card is not led by its own history.
  const deploymentsOf = (app) =>
    instances
      .filter((i) => i.app === app)
      .sort((a, b) => (a.desiredState === "absent" ? 1 : 0)
                    - (b.desiredState === "absent" ? 1 : 0));

  return {
    catalog,
    instances,
    deploymentsOf,
    loading,
    failure,
    load,
    note,
    setNote,
    // The install dialog's subject, and the remove dialog's. Held here
    // because `install` and `remove` close them, and a screen that owned the
    // open/closed state while this owned the action would have two places to
    // look when a dialog stayed open after a failure.
    chosen,
    setChosen,
    removing,
    setRemoving,
    removeBusy,
    install,
    remove,
    update,
    setAutoUpdate,
    adopting,
    setAdopting,
    reverting,
    setReverting,
    planAdoption,
    adopt,
    revert,
    copiesFor: (app) => copiesOf(catalog, app),
    requirementsFor,
    // Null when this console cannot write to the control plane, which is what
    // the Remove button is gated on.
    canWrite: Boolean(controlWrite),
    nameOf,
    countFor,
  };
}
